import { List, ListItem, ListItemButton, ListItemIcon, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import { signOut } from '@aws-amplify/auth';
import { format } from 'date-fns';
import { NextRouter, useRouter } from 'next/router';
import AppDrawer from 'packages/workspace/components/common/AppDrawer';
import BackdropLoader from 'packages/workspace/components/common/BackdropLoader';
import ChangePassword from 'packages/workspace/components/common/ChangePassword';
import Footer from 'packages/workspace/components/general/Footer';
import { Loader } from 'packages/workspace/design/widgets/ui/Loader';
import { AbilityContext } from 'packages/workspace/modules/helpers/contexts/abilities';
import { useAuthCtx } from 'packages/workspace/modules/helpers/contexts/auth';
import { SocketProvider } from 'packages/workspace/modules/helpers/contexts/socket';
import { Http } from 'packages/workspace/modules/prelude/http';
import { createLogger } from 'packages/workspace/modules/prelude/logger/logger';
import type { IamPrincipal } from 'packages/workspace/services/models/session';
import useAbilitiesStore, { AbilityStore } from 'packages/workspace/services/store/abilities-store';
import { InternalConversationStore } from 'packages/workspace/services/store/omnichannel/internal-conversation-store';
import useUiState, { uiStore } from 'packages/workspace/services/store/ui-store';
import { FC, useEffect, useMemo, useState } from 'react';
import { MdAccountCircle, MdFolderSpecial, MdDashboard, MdEqualizer, MdForum, MdLogout, MdPeople, MdTune } from 'react-icons/md';
import { useIdleTimer } from 'react-idle-timer';
import { useQueryClient } from 'react-query';
import type { ResponseFormat } from '../../../services/models/global-models';
import useSession, { sessionStore } from '../../../services/store/session-store';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useLogoutLogs } from 'packages/workspace/services/fetchers/workspace-info';
const FssIcon = dynamic(() => import('packages/workspace/components/fss/FssIcon'));

interface GenericFCProps {
  children?: React.ReactNode;
}

interface NavProps {
  onProfileClick: () => void;
}

export const Navigation: FC<NavProps> = ({ onProfileClick }) => {
  const router: NextRouter = useRouter();
  const { disableNavigation } = useUiState();
  const queryClient = useQueryClient();
  const fileLabel = 'base.tsx';
  const logger = createLogger({
    fileLabel,
  });
  const { mutateAsync: mutateLogoutLogs } = useLogoutLogs();
  const handleSignOut = async () => {
    try {
      await Http.Request<ResponseFormat<IamPrincipal>>('GET', '/workspaces/api/v1/authn.logout');
      mutateLogoutLogs({ tag: 'base' });
      await signOut();
      AbilityStore.clearRules();
      uiStore.setSuccessMessage('Successfully Logged Out, Its Safe to Close Your Browser Tab');
      sessionStore.unsetDiscoveryParams();
      queryClient.invalidateQueries();
      router.push('/auth/auth.page');
    } catch (error) {
      uiStore.setErrorMessage(error.errors.toString());
      logger.error('error signing out: ', error);
    }
  };

  return (
    <nav className="rhs-left-nav sidebar-nav">
      <div className="nav-top-section">
        <List sx={{ padding: 0 }}>
          <ListItem disablePadding className={`${router.pathname}` == '/' ? 'nav-list active' : 'nav-list'} sx={{ width: 'unset' }}>
            <Tooltip title="Launchpad" placement="right">
              <ListItemButton onClick={() => router.push('/')} sx={{ padding: 0, maxWidth: '20px' }}>
                <ListItemIcon>
                  <MdDashboard className="nav-list-icon" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <ListItem disablePadding className={`${router.pathname.includes('/omnichannel') ? 'nav-list active' : 'nav-list'}`} sx={{ width: 'unset' }}>
            <Tooltip title="Omnichannel" placement="right">
              <ListItemButton onClick={() => router.push('/omnichannel')} sx={{ padding: 0, maxWidth: '20px' }}>
                <ListItemIcon>
                  <MdForum className="nav-list-icon" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <ListItem disablePadding className={`${router.pathname === '/helproom' ? 'nav-list active' : 'nav-list'}`} sx={{ width: 'unset' }}>
            <Tooltip title="Helproom" placement="right">
              <ListItemButton onClick={() => router.push('/helproom')} sx={{ padding: 0, maxWidth: '20px' }}>
                <ListItemIcon>
                  <MdPeople className="nav-list-icon" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <ListItem disablePadding className={`${router.pathname === '/reports' ? 'nav-list active' : 'nav-list'}`} sx={{ width: 'unset' }}>
            <Tooltip title="Reports" placement="right">
              <ListItemButton onClick={() => router.push('/reports')} sx={{ padding: 0, maxWidth: '20px' }}>
                <ListItemIcon>
                  <MdEqualizer className="nav-list-icon" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <FssIcon />
          {/* <ListItem disablePadding className={`${router.pathname === '/resources-dashboard' ? 'nav-list active' : 'nav-list'}`} sx={{ width: 'unset' }}>
            <Tooltip title="Resources Dashboard" placement="right">
              <ListItemButton onClick={() => router.push('/resources-dashboard')} sx={{ padding: 0, maxWidth: '20px' }}>
                <ListItemIcon>
                  <MdFolderSpecial className="nav-list-icon" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem> */}
        </List>
      </div>
      <div className="nav-bottom-section">
        <List sx={{ padding: 0 }}>
          <ListItem disablePadding className={`${router.pathname.includes('/console') ? 'nav-list active' : 'nav-list'}`} sx={{ width: 'unset' }}>
            <Tooltip title="Management Portal" placement="right">
              <ListItemButton onClick={() => router.push('/console')} sx={{ padding: 0, maxWidth: '20px' }}>
                <ListItemIcon>
                  <MdTune className="nav-list-icon" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <ListItem disablePadding className={'nav-list'} sx={{ width: 'unset' }}>
            <Tooltip title="Profile" placement="right">
              <ListItemButton onClick={onProfileClick} sx={{ padding: 0, maxWidth: '20px' }}>
                <ListItemIcon>
                  <MdAccountCircle className="nav-list-icon" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <ListItem disablePadding className={'nav-list'} sx={{ width: 'unset' }}>
            <Tooltip title="Logout" placement="right">
              <ListItemButton onClick={handleSignOut} sx={{ padding: 0, maxWidth: '20px' }}>
                <ListItemIcon>
                  <MdLogout className="nav-list-icon" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </div>
    </nav>
  );
};

interface IHeaderProps {
  lastlogin: number | undefined;
  user_name: string;
  header_name: string;
}

const Header: FC<IHeaderProps> = ({ lastlogin = Date.now(), user_name, header_name }) => {
  return (
    // <header className="bg-tc-1  ml-16  w-full h-16 fixed top-0 flex items-center flex justify-between pr-20 px-4 text-tc-3 my-3">
    <header className="w-full flex items-center flex justify-between px-4 text-tc-3 py-6">
      <h1 className={'text-tc-4 font-interbold text-3xl'}>Launchpad</h1>
      <Typography variant="h4" gutterBottom className="module-title">
        {header_name}
      </Typography>
      <p className="text-xs">
        Welcome <span className="font-bold">{user_name}</span>, Last Login : <span className="font-interbold">{format(new Date(lastlogin), 'MM/dd/yyyy hh:mm a')}</span>
      </p>
    </header>
  );
};

const Main: FC = (props: GenericFCProps) => {
  return (
    <main className="bg-tc-1">
      <div className="bg-tc-1 min-h-screen pl-16 flex flex-col justify-between">{props.children}</div>
    </main>
  );
};

export interface BasePageLayoutProps {
  includeFooter?: boolean;
  includeHeader?: boolean;
  header_name: string;
  title?: string;
}

const tenMinutes = 10 * 60 * 1000;
const oneMinute = 1 * 60 * 1000;

export const BasePageLayout: FC<BasePageLayoutProps & GenericFCProps> = (props) => {
  const { loading, system_user, cognito_user } = useAuthCtx();
  const { includeFooter = true, includeHeader = true, title } = props;
  const [idle, setIdle] = useState<boolean>(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const { ability } = useAbilitiesStore();
  const queryClient = useQueryClient();
  const [drawerState, setDrawerState] = useState({
    anchor: { right: false },
  });
  const router = useRouter();
  const session = useSession();

  useEffect(() => InternalConversationStore.clearActiveConnections(), []);

  const autoLogoutConfiguration = useMemo(
    () => ({
      enabled: session?.discoveryParams?.workspaceSettings?.auto_logout_enabled ?? false,
      timeout: session?.discoveryParams?.workspaceSettings?.auto_logout_idle_timeout ? parseInt(session?.discoveryParams?.workspaceSettings?.auto_logout_idle_timeout) * 1000 : tenMinutes,
      promptTimeout: session?.discoveryParams?.workspaceSettings?.auto_logout_prompt_timeout ? parseInt(session?.discoveryParams?.workspaceSettings?.auto_logout_prompt_timeout) * 1000 : oneMinute,
    }),
    [
      session?.discoveryParams?.workspaceSettings?.auto_logout_enabled,
      session?.discoveryParams?.workspaceSettings?.auto_logout_idle_timeout,
      session?.discoveryParams?.workspaceSettings?.auto_logout_prompt_timeout,
    ]
  );
  const onPrompt = () => setIdle(true);
  /**
   *IDLE Handler
   */
  const onIdle = async () => {
    try {
      await Http.Request<ResponseFormat<IamPrincipal>>('GET', '/workspaces/api/v1/authn.logout');
      await signOut();
      AbilityStore.clearRules();
      uiStore.setSuccessMessage('Successfully Logged Out Due to Inactivity');
      sessionStore.unsetDiscoveryParams();
      queryClient.invalidateQueries();
      router.push('/auth/auth.page');
    } catch (error) {
      uiStore.setErrorMessage(error.errors?.toString());
    }
  };
  /**
   *Using the hook
   */
  const { activate, start } = useIdleTimer({
    onIdle,
    onPrompt,
    startManually: true,
    crossTab: true,
    timeout: autoLogoutConfiguration.timeout,
    promptTimeout: autoLogoutConfiguration.promptTimeout,
    eventsThrottle: 1000,
    startOnMount: false,
    syncTimers: 0,
    leaderElection: false,
    events: ['mousemove', 'keydown', 'wheel', 'DOMMouseScroll', 'mousewheel', 'mousedown', 'touchstart', 'touchmove', 'MSPointerDown', 'MSPointerMove', 'visibilitychange'],
  });

  useEffect(() => {
    if (autoLogoutConfiguration.enabled) {
      start();
    }
  }, [autoLogoutConfiguration.enabled, start]);

  const stillHere = () => {
    setIdle(false);
    activate();
  };

  /**
   *Opening Change Pwd drawer
   * @param anchor
   * @param open
   */
  const toggleDrawer = (anchor: string, open: boolean) => {
    setDrawerState({
      ...drawerState,
      anchor: { ...drawerState.anchor, [anchor]: open },
    });
  };
  const onProfileClick = () => {
    setDrawerOpen(true);
    toggleDrawer('right', true);
  };
  return (
    <>
      {title && (
        <Head>
          <title>{title}</title>
        </Head>
      )}
      <Navigation onProfileClick={onProfileClick} />
      {idle && (
        <Dialog open={idle}>
          <DialogTitle>Your session is about to end.</DialogTitle>
          <DialogContent>
            <DialogContentText>Do you want to continue?</DialogContentText>
            <DialogContentText>You will be logged out in one minute unless you click extend</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={stillHere}>Extend</Button>
          </DialogActions>
        </Dialog>
      )}
      {!loading && system_user && cognito_user ? (
        <SocketProvider>
          <AbilityContext.Provider value={ability}>
            <BackdropLoader />
            <Main>
              {includeHeader && <Header lastlogin={system_user?.lastlogin} user_name={system_user.displayName} header_name={props.header_name} />}
              <div className="flex-1">
                {props.children}
                <AppDrawer className="rhs-drawer wd-520" open={drawerState.anchor.right}>
                  <ChangePassword
                    userId={system_user.id}
                    isDrawerOpen={isDrawerOpen}
                    onClose={() => {
                      setDrawerOpen(false);
                      toggleDrawer('right', false);
                    }}
                  />
                </AppDrawer>
              </div>
              {includeFooter && <Footer />}
            </Main>
          </AbilityContext.Provider>
        </SocketProvider>
      ) : (
        <Loader />
      )}
    </>
  );
};
